import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/SEO";
import { useLang } from "@/context/LanguageContext";
import { Search, Package, Truck, CheckCircle2, Clock, XCircle, MapPin } from "lucide-react";
import api from "@/lib/api";
import { trackContact } from "@/lib/trackContact";

const STATUS_META = {
  processing: { key: "track_status_processing", color: "bg-amber-100 text-amber-700 border-amber-200", Icon: Clock },
  confirmed: { key: "track_status_confirmed", color: "bg-blue-100 text-blue-700 border-blue-200", Icon: CheckCircle2 },
  shipped: { key: "track_status_shipped", color: "bg-emerald-100 text-emerald-700 border-emerald-200", Icon: Truck },
  delivered: { key: "track_status_delivered", color: "bg-emerald-100 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
  cancelled: { key: "track_status_cancelled", color: "bg-red-100 text-red-700 border-red-200", Icon: XCircle },
};
const PAY_KEYS = { pending: "pay_status_pending", paid: "pay_status_paid", refunded: "pay_status_refunded" };

export default function TrackOrder() {
  const { lang, t } = useLang();
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const track = async (e) => {
    e?.preventDefault?.();
    setError("");
    setResult(null);
    const c = code.trim().toUpperCase();
    if (c.length < 6) { setError(t("track_error_short")); return; }
    setLoading(true);
    try {
      const r = await api.get(`/orders/track/${encodeURIComponent(c)}`, {
        params: email ? { email: email.trim() } : {},
      });
      setResult(r.data);
    } catch (err) {
      setError(err.response?.data?.detail || t("track_error_notfound"));
    } finally {
      setLoading(false);
    }
  };

  const status = result ? STATUS_META[result.status] || STATUS_META.processing : null;
  const localeDate = lang === "it" ? "it-IT" : lang === "fr" ? "fr-FR" : lang === "de" ? "de-DE" : lang === "es" ? "es-ES" : "en-GB";

  return (
    <div className="min-h-screen bg-xd-light" data-testid="page-track-order">
      <SEO
        title={t("track_seo_title")}
        description={t("track_seo_desc")}
        path="/traccia-ordine"
        lang={lang}
        noindex
      />
      <Header />
      <section className="bg-xd-dark text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
          <div className="font-mono-tech text-xs uppercase tracking-widest text-xd-cyan">{t("track_kicker")}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-2 tracking-tight">{t("track_h1")}</h1>
          <p className="mt-3 text-slate-300 max-w-xl">{t("track_intro")}</p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <form onSubmit={track} className="bg-white border border-xd-border rounded-2xl p-6 sm:p-8 space-y-4 shadow-sm">
          <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <div>
              <label className="text-xs font-mono-tech uppercase tracking-widest text-xd-muted">{t("track_code_label")}</label>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder={t("track_code_placeholder")}
                data-testid="track-code-input"
                className="mt-1 w-full font-mono-tech tracking-widest border border-xd-border rounded-lg px-3 py-2.5 focus:outline-none focus:border-xd-cyan"
              />
            </div>
            <div>
              <label className="text-xs font-mono-tech uppercase tracking-widest text-xd-muted">{t("track_email_label")} <span className="text-xd-muted lowercase">{t("track_email_optional")}</span></label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("track_email_placeholder")}
                data-testid="track-email-input"
                className="mt-1 w-full border border-xd-border rounded-lg px-3 py-2.5 focus:outline-none focus:border-xd-cyan"
              />
            </div>
            <button
              type="submit"
              data-testid="track-submit-btn"
              disabled={loading}
              className="btn-cta rounded-lg px-6 py-2.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Search className="w-4 h-4" /> {loading ? t("track_searching") : t("track_submit")}
            </button>
          </div>
          {error && <div className="text-sm text-red-600" data-testid="track-error">{error}</div>}
        </form>

        {result && (
          <div className="mt-6 bg-white border border-xd-border rounded-2xl p-6 sm:p-8" data-testid="track-result">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
              <div>
                <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted">{t("track_order_label")}</div>
                <div className="font-display text-2xl font-semibold text-xd-text mt-0.5">{result.order_number}</div>
                <div className="text-xs text-xd-muted mt-1">
                  {new Date(result.created_at).toLocaleString(localeDate)} · {result.items_count} {t("track_items")} · € {result.total?.toFixed?.(2)}
                </div>
              </div>
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm font-medium ${status?.color || ""}`} data-testid="track-status">
                {status?.Icon && <status.Icon className="w-4 h-4" />}
                {status?.key ? t(status.key) : result.status}
              </span>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="border border-xd-border rounded-xl p-4">
                <div className="text-xs font-mono-tech uppercase tracking-widest text-xd-muted flex items-center gap-1">
                  <Package className="w-3 h-3" /> {t("track_payment")}
                </div>
                <div className="text-sm font-medium text-xd-text mt-1">{PAY_KEYS[result.payment_status] ? t(PAY_KEYS[result.payment_status]) : result.payment_status}</div>
              </div>
              <div className="border border-xd-border rounded-xl p-4">
                <div className="text-xs font-mono-tech uppercase tracking-widest text-xd-muted flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {t("track_destination")}
                </div>
                <div className="text-sm font-medium text-xd-text mt-1">{[result.shipping_city, result.shipping_country].filter(Boolean).join(" · ") || "—"}</div>
              </div>
              {result.tracking_number && (
                <div className="border border-xd-border rounded-xl p-4 sm:col-span-2">
                  <div className="text-xs font-mono-tech uppercase tracking-widest text-xd-muted flex items-center gap-1">
                    <Truck className="w-3 h-3" /> {t("track_courier")}
                  </div>
                  <div className="text-sm font-medium text-xd-text mt-1 font-mono-tech">{result.tracking_number}</div>
                </div>
              )}
            </div>

            <div className="mt-6 text-xs text-xd-muted">
              {t("track_help")} <a className="text-xd-cyanHover hover:underline" href="https://wa.me/393511294525" data-testid="track-whatsapp-link" onClick={() => trackContact("whatsapp", "track_order")}>WhatsApp +39 351 129 4525</a> {t("track_help_suffix")} <span className="font-mono-tech text-xd-text">{result.tracking_code}</span>.
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
